import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import './AddMeeting.scss';
import { Dispatch } from 'src/configs/store';
import AddMeetingContext, { StepError } from './Context/AddMeeting.context';
import Button from 'src/components/Button/Button';
import AddMeetingFormStep1 from 'src/components/AddMeetingForm/AddMeetingFormStep1';
import AddMeetingFormStep2 from 'src/components/AddMeetingForm/AddMeetingFormStep2';
import AddMeetingFormStep3 from 'src/components/AddMeetingForm/AddMeetingFormStep3';
import Stepper from 'src/components/AddMeetingForm/Stepper/Stepper';

const STEPS = [0, 1, 2, 3];
const STEP_COMPONENTS = [AddMeetingFormStep1, AddMeetingFormStep2, AddMeetingFormStep3];

export default function AddMeeting() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [stepErrors, setStepErrors] = useState<StepError[]>([]);
  const [formData, setFormData] = useState({});

  const formRef = useRef(null);
  const { t } = useTranslation('common');
  const dispatch = useDispatch<Dispatch>();

  const StepComponent = STEP_COMPONENTS[activeStep];

  const onFormSubmit = (e) => {
    e.preventDefault();

    // Trigger dirtiness to avoid accidental route change.
    dispatch.events.changeDirtiness(true);

    const _formData = {};
    // Get all elements of the form so we can collect all data.
    for (const { name, value } of e.target.elements) {
      if (name) {
        _formData[name] = value;
      }
    }
    setFormData(Object.assign(formData, _formData));

    // We have errors, we can't change step.
    if (stepErrors.length > 0) return;

    const newStep = Number(e.target.nextStep.value);
    // We don't want to go out of range of the steps.
    if (newStep < 0 || newStep > STEPS.length - 1) return;

    setActiveStep(newStep);
  };

  const context = useMemo(() => {
    return {
      activeStep,
      stepErrors,
      setStepErrors,
      errorsCount: stepErrors.length,
      formData
    };
  }, [activeStep, stepErrors, formData]);

  return (
    <AddMeetingContext.Provider value={ context }>
      <div className="add-meeting">
        <div className="add-meeting__header">
          <div className="add-meeting__title">{ t('addMeeting.setup') }</div>

          <Button className="btn--primary add-meeting__import-btn" text={ t('addMeeting.import') } />
        </div>

        <form className="add-meeting-form" onSubmit={ onFormSubmit } ref={ formRef }>
          { StepComponent && <StepComponent /> }

          <div className="add-meeting__stepper">
            <Stepper steps={ STEPS } />
          </div>
        </form>
      </div>
    </AddMeetingContext.Provider>
  );
}
